// Card with a gradient header (icon + title + optional subtitle) and a flex-column content body that scrolls.
import React from "react";
import { Card, CardContent, Stack, Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { tokens } from "../../theme";

const SectionCard = ({
  icon,
  title,
  subtitle,
  headerBg,
  headerDirection = "column",
  headerRight,
  children,
  sx,
}) => (
  <Card
    elevation={0}
    sx={{
      background: tokens.surface,
      color: tokens.text,
      border: `1px solid ${tokens.border}`,
      borderRadius: "14px",
      overflow: "hidden",
      boxShadow: tokens.shadowLg,
      // Flex column so the gradient header keeps its natural height and the
      // body below it absorbs any extra height a parent hands this card.
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      ...sx,
    }}
  >
    {/* Gradient header row */}
    <Stack
      direction={headerDirection === "row" ? "row" : "column"}
      alignItems="center"
      gap={headerDirection === "row" ? 1.5 : 0.5}
      sx={{
        px: 3,
        py: 2.5,
        background:
          headerBg || `linear-gradient(135deg, ${tokens.accent} 0%, ${tokens.accent2} 100%)`,
        textAlign: headerDirection === "row" ? "left" : "center",
        // Never give up height to the scrolling body below it.
        flexShrink: 0,
      }}
    >
      {icon && <Box sx={{ mb: headerDirection === "row" ? 0 : 0.5, flexShrink: 0 }}>{icon}</Box>}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>{subtitle}</Typography>
        )}
      </Box>
      {headerRight && <Box sx={{ flexShrink: 0, ml: "auto" }}>{headerRight}</Box>}
    </Stack>

    {/* Card body.
        `flex: 1` + `minHeight: 0` so that when a parent stretches this card to
        a fixed height, the BODY takes the slack and scrolls rather than the
        card growing past its container. When the parent does not constrain the
        height this is inert and the body stays content-sized. */}
    <CardContent
      sx={{
        p: 2.5,
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        background: tokens.surface,
        color: tokens.text,
      }}
    >
      {children}
    </CardContent>
  </Card>
);

SectionCard.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  headerBg: PropTypes.string,
  headerDirection: PropTypes.oneOf(["row", "column"]),
  headerRight: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
};

export default SectionCard;
